import { useCallback, useEffect } from "react";
import { PANEL_DEFAULT_WIDTH_PX } from "./useChatPanelState";

const PANEL_MIN_WIDTH_PX = PANEL_DEFAULT_WIDTH_PX;
const PANEL_MAX_WIDTH_VIEWPORT_RATIO = 0.9;
const OVERLAY_CONTENT_MIN_WIDTH_PX = 320;

const applyPanelWidthToDom = (widthPx: number) => {
  const contentPushWidthPx = Math.min(
    widthPx,
    window.innerWidth - OVERLAY_CONTENT_MIN_WIDTH_PX,
  );

  document.body.style.setProperty("--tcw-panel-width", `${widthPx}px`);
  document.body.style.setProperty(
    "--tcw-content-push-width",
    `${contentPushWidthPx}px`,
  );
};

export const usePanelResize = (
  panelWidthInPixels: number,
  setPanelWidthInPixels: (widthPx: number) => void,
) => {
  useEffect(() => {
    applyPanelWidthToDom(panelWidthInPixels);
    document.body.classList.add("has-ai-chat");
  }, [panelWidthInPixels]);

  useEffect(() => {
    const handleWindowResize = () => applyPanelWidthToDom(panelWidthInPixels);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [panelWidthInPixels]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      document.body.classList.add("tcw-resizing");

      const onMouseMove = (moveEvent: MouseEvent) => {
        const maxWidthPx = window.innerWidth * PANEL_MAX_WIDTH_VIEWPORT_RATIO;
        const newWidth = Math.min(
          maxWidthPx,
          Math.max(PANEL_MIN_WIDTH_PX, window.innerWidth - moveEvent.clientX),
        );
        setPanelWidthInPixels(newWidth);
      };

      const onMouseUp = () => {
        document.body.classList.remove("tcw-resizing");
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [setPanelWidthInPixels],
  );

  return { handleResizeStart };
};
